import type { CSSProperties } from 'react';

/** Icons available for `DomainGlyph` — kept narrow so it stays meaningful. */
export type GlyphIcon = 'shield' | 'database' | 'cpu' | 'network' | 'activity';

/* ──────────────────────────────────────────────────────────────────────
 * Lucide-style stroke icons (subset used by Variant B).
 * Stroke-only, currentColor — sized via `size` prop.
 * Each path is taken verbatim from the design source so the visual
 * language matches pixel-for-pixel.
 * ──────────────────────────────────────────────────────────────────── */

const PATHS: Record<string, string> = {
  shield: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z',
  database: 'M12 8c4.4 0 8-1.3 8-3s-3.6-3-8-3-8 1.3-8 3 3.6 3 8 3ZM4 5v14c0 1.7 3.6 3 8 3s8-1.3 8-3V5M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3',
  cpu: 'M6 6h12v12H6zM9 9h6v6H9zM9 1v3M15 1v3M9 20v3M15 20v3M1 9h3M1 15h3M20 9h3M20 15h3',
  network: 'M12 3a3 3 0 1 0 0 6 3 3 0 0 0 0-6ZM5 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM19 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM12 9v3M12 12 6 15M12 12l6 3',
  activity: 'M3 12h4l3 8 4-16 3 8h4',
  arrowup: 'M12 19V5M5 12l7-7 7 7',
  arrowright: 'M5 12h14M13 5l7 7-7 7',
  chevright: 'M9 6l6 6-6 6',
  check: 'M20 6 9 17l-5-5',
  clock: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 6v6l4 2',
  layers: 'M12 2 2 7l10 5 10-5-10-5ZM2 17l10 5 10-5M2 12l10 5 10-5',
  target: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 18a6 6 0 1 0 0-12 6 6 0 0 0 0 12ZM12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
  play: 'M6 4l14 8-14 8V4Z',
  zap: 'M13 2 4 14h7l-1 8 9-12h-7l1-8Z',
  lock: 'M6 11h12v9a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-9ZM8 11V8a4 4 0 0 1 8 0v3',
  brain: 'M9.5 3a3 3 0 0 0-3 3 3 3 0 0 0-2 5 3 3 0 0 0 2 5 3 3 0 0 0 5.5 1.5V3.5A2.5 2.5 0 0 0 9.5 3ZM14.5 3a3 3 0 0 1 3 3 3 3 0 0 1 2 5 3 3 0 0 1-2 5 3 3 0 0 1-5.5 1.5',
  trophy: 'M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0V4ZM7 5H4v2a3 3 0 0 0 3 3M17 5h3v2a3 3 0 0 1-3 3',
  filecheck: 'M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9l-6-6ZM14 3v6h6M9 15l2 2 4-4',
  flame: 'M12 2c1 3-1 5-2 6-1.2 1.2-2 2.6-2 4.3A4 4 0 0 0 16 14c0-1-.3-2-1-3 1.7.5 3 2.3 3 4.3a6 6 0 0 1-12 0c0-3.3 2.5-5.6 4-7.6C11 6 12 4 12 2Z',
  dumbbell: 'M6.5 6.5 17.5 17.5M3 7l1.5-1.5M5.5 4.5 9 8M15 16l3.5 3.5M16.5 13 20 16.5M19.5 18 21 16.5',
  bookmark: 'M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1Z',
  settings: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z M19.4 13a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-2.9 1.2 2 2 0 1 1-4 0 1.7 1.7 0 0 0-2.9-1.2l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.7 1.7 0 0 0 2.6 11 2 2 0 1 1 2.6 7a1.7 1.7 0 0 0 2.9-1.2l-.1-.1A2 2 0 1 1 8.1 2.9l.1.1A1.7 1.7 0 0 0 11 1.6a2 2 0 1 1 4 0 1.7 1.7 0 0 0 2.9 1.2l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0 1.2 2.9 2 2 0 1 1 0 4 1.7 1.7 0 0 0-1.5 1Z',
  calendar: 'M5 5h14a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1ZM16 3v4M8 3v4M4 10h16',
  sparkles: 'M12 3l1.8 4.7L18.5 9.5 13.8 11.3 12 16l-1.8-4.7L5.5 9.5l4.7-1.8L12 3ZM19 14l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8.8-2.2Z',
};

interface IconProps {
  name: keyof typeof PATHS | string;
  size?: number;
  fill?: boolean;
  className?: string;
  style?: CSSProperties;
  strokeWidth?: number;
}

/** Inline stroke icon, mirrors `CMIco` from the design source. */
export function CMIcon({ name, size = 18, fill = false, className = '', style, strokeWidth = 1.9 }: IconProps) {
  const d = PATHS[name] ?? '';
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill ? 'currentColor' : 'none'}
      stroke={fill ? 'none' : 'currentColor'}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Domain Glyph — rounded tile with diagonal gradient + centered icon.
 * ──────────────────────────────────────────────────────────────────── */
interface GlyphProps {
  icon: GlyphIcon;
  gradient: [string, string];
  size?: number;
  radius?: number;
  iconScale?: number;
  className?: string;
}

export function DomainGlyph({ icon, gradient, size = 52, radius = 14, iconScale = 0.48, className = '' }: GlyphProps) {
  const iconSize = Math.round(size * iconScale);
  return (
    <div
      className={`relative inline-flex items-center justify-center text-white shadow-sm ${className}`}
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        background: `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})`,
      }}
      aria-hidden="true"
    >
      {/* Soft top-right radial sheen */}
      <span
        className="absolute inset-0 pointer-events-none"
        style={{
          borderRadius: radius,
          background:
            'radial-gradient(circle at 78% 22%, rgba(255,255,255,0.32) 0%, rgba(255,255,255,0) 55%)',
        }}
      />
      <CMIcon name={icon} size={iconSize} strokeWidth={1.8} />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Ring Gauge — 270° arc, gradient stroke, target tick marker.
 * Animates `stroke-dasharray` on prop changes (CSS transition).
 * ──────────────────────────────────────────────────────────────────── */
interface RingProps {
  value: number;       // 0..100
  target?: number;     // 0..100 — pass mark; shows amber tick when set
  size?: number;
  stroke?: number;
  className?: string;
}

export function RingGauge({ value, target = 70, size = 196, stroke = 16, className = '' }: RingProps) {
  const r = (size - stroke) / 2;
  const cx = size / 2;
  const circ = 2 * Math.PI * r;
  const arcFraction = 0.75; // 270°
  const arcLen = circ * arcFraction;
  const pct = Math.max(0, Math.min(1, value / 100));
  const targetPct = Math.max(0, Math.min(1, target / 100));
  const startAngle = 135;
  const targetAngleDeg = startAngle + targetPct * arcFraction * 360;
  const rad = ((targetAngleDeg - 90) * Math.PI) / 180;
  const tx = cx + r * Math.cos(rad);
  const ty = cx + r * Math.sin(rad);
  const progressLen = arcLen * pct;
  const gid = `ring-grad-${size}-${value}-${target}`;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className={className} aria-hidden="true">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#60A5FA" />
          <stop offset="55%" stopColor="#2563EB" />
          <stop offset="100%" stopColor="#7C3AED" />
        </linearGradient>
      </defs>
      {/* Track */}
      <circle
        cx={cx}
        cy={cx}
        r={r}
        fill="none"
        className="stroke-slate-200 dark:stroke-white/10"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={`${arcLen} ${circ}`}
        transform={`rotate(${startAngle} ${cx} ${cx})`}
      />
      {/* Progress */}
      <circle
        cx={cx}
        cy={cx}
        r={r}
        fill="none"
        stroke={`url(#${gid})`}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={`${progressLen} ${circ}`}
        transform={`rotate(${startAngle} ${cx} ${cx})`}
        style={{ transition: 'stroke-dasharray 0.9s cubic-bezier(.3,.7,.3,1)' }}
      />
      {/* Target tick (pass mark) */}
      <circle
        cx={tx}
        cy={ty}
        r={stroke / 2 + 2}
        className="fill-white dark:fill-slate-900"
        stroke="#F59E0B"
        strokeWidth={2.5}
      />
    </svg>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Progress bar with optional pass-mark tick at 70%.
 * ──────────────────────────────────────────────────────────────────── */
interface BarProps {
  value: number;          // 0..100
  fillClass: string;      // tailwind background class
  tickAt?: number | null; // 0..100; renders a vertical tick when set
  className?: string;
}

export function MiniBar({ value, fillClass, tickAt = null, className = '' }: BarProps) {
  const w = Math.max(0, Math.min(100, value));
  return (
    <div className={`relative h-2 w-full rounded-full bg-slate-200/70 dark:bg-white/10 overflow-hidden ${className}`}>
      <div
        className={`h-full rounded-full transition-[width] duration-700 ease-out ${fillClass}`}
        style={{ width: `${w}%` }}
      />
      {tickAt !== null && (
        <span
          className="absolute top-0 h-full w-0.5 bg-slate-500/55 dark:bg-white/35"
          style={{ left: `${Math.max(0, Math.min(100, tickAt))}%` }}
          aria-hidden="true"
        />
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Mono number — for scores, counts, percentages.
 * ──────────────────────────────────────────────────────────────────── */
export function Mono({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={`font-mono tabular-nums tracking-tight ${className}`}
      style={{ fontFeatureSettings: '"tnum"' }}
    >
      {children}
    </span>
  );
}
